import { useCallback, useEffect, useRef } from 'react'
import {
  stripRedundantSectionHeadingFromBody,
  type ComplianceDocumentSectionRecord,
} from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'
import { MoreHorizontal, PencilIcon, RefreshCw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DocumentEditor } from '@/components/document-editor'
import { getSectionHeadingLabel } from './complianceDocumentEditorUtils'
import type {
  ComplianceDocumentI18nNamespace,
  ComplianceDocumentTranslationKeyPrefix,
} from './complianceDocumentEditorTypes'

type SectionEditorRegistration = {
  sectionId: string
  editorRef: React.MutableRefObject<Editor | null>
}

type ComplianceDocumentSectionBlockProps = {
  section: ComplianceDocumentSectionRecord
  sectionTitle: string
  sectionDomIdPrefix: string
  translationKeyPrefix: ComplianceDocumentTranslationKeyPrefix
  i18nNamespace: ComplianceDocumentI18nNamespace
  imageUploadScopeId?: string
  contentSyncKey: number
  isGenerating: boolean
  isGeneratingThisSection: boolean
  onRegister: (registration: SectionEditorRegistration) => void
  onUnregister: (sectionId: string) => void
  onRemoveCustomSection: (sectionId: string) => void
  onRenameRequest: (sectionId: string) => void
  onRegenerateSection?: (sectionId: string) => void
  onSectionFocus: (sectionId: string) => void
  onSectionEditorReady: (sectionId: string) => void
  onPlaceholderMarksApplied?: () => void
  onContentChange: () => void
  onEditorBlur: () => void
  showEditor: boolean
  bubbleMenuContent?: React.ReactNode
  bubbleMenuHidden?: boolean
}

export function ComplianceDocumentSectionBlock({
  section,
  sectionTitle,
  sectionDomIdPrefix,
  translationKeyPrefix,
  i18nNamespace,
  imageUploadScopeId,
  contentSyncKey,
  isGenerating,
  isGeneratingThisSection,
  onRegister,
  onUnregister,
  onRemoveCustomSection,
  onRenameRequest,
  onRegenerateSection,
  onSectionFocus,
  onSectionEditorReady,
  onPlaceholderMarksApplied,
  onContentChange,
  onEditorBlur,
  showEditor,
  bubbleMenuContent,
  bubbleMenuHidden,
}: ComplianceDocumentSectionBlockProps) {
  const { t } = useTranslation(i18nNamespace)
  const editorRef = useRef<Editor | null>(null)
  const showRegenerateSection = onRegenerateSection != null

  useEffect(() => {
    onRegister({ sectionId: section.sectionId, editorRef })
    return () => {
      onUnregister(section.sectionId)
    }
  }, [section.sectionId, onRegister, onUnregister])

  const handleEditorReady = useCallback(
    (_editor: Editor) => {
      onSectionEditorReady(section.sectionId)
    },
    [onSectionEditorReady, section.sectionId]
  )

  const handleEditorFocus = useCallback(() => {
    onSectionFocus(section.sectionId)
  }, [onSectionFocus, section.sectionId])

  const handleRenameRequest = useCallback(() => {
    onRenameRequest(section.sectionId)
  }, [onRenameRequest, section.sectionId])

  const handleRegenerateSection = useCallback(() => {
    onRegenerateSection?.(section.sectionId)
  }, [onRegenerateSection, section.sectionId])

  return (
    <section
      id={`${sectionDomIdPrefix}-section-${section.sectionId}`}
      className={cn(
        'flex scroll-mt-4 flex-col gap-3 border-border transition-colors duration-300',
        showEditor && 'border-b',
        isGeneratingThisSection && 'bg-primary/5'
      )}
    >
      <div className='flex items-start justify-between gap-2 px-4 pt-4'>
        <h2 className='min-w-0 flex-1 text-lg font-semibold'>
          {getSectionHeadingLabel(section, sectionTitle)}
        </h2>
        <div className='flex shrink-0 items-center gap-1'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8'
                disabled={isGenerating}
                aria-label={t(`${translationKeyPrefix}.sectionActions.menuAria`)}
              >
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {showRegenerateSection ? (
                <DropdownMenuItem onClick={handleRegenerateSection}>
                  <RefreshCw />
                  {t(`${translationKeyPrefix}.sectionActions.regenerateSection`)}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onClick={handleRenameRequest}>
                <PencilIcon />
                {t(`${translationKeyPrefix}.sectionActions.rename`)}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {section.kind === 'custom' ? (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8'
              aria-label={t(`${translationKeyPrefix}.removeAppendix`)}
              disabled={isGenerating}
              onClick={() => {
                onRemoveCustomSection(section.sectionId)
              }}
            >
              <Trash2 className='size-4' />
            </Button>
          ) : null}
        </div>
      </div>

      {showEditor ? (
        <DocumentEditor
          embedded
          disabled={isGenerating}
          markdown={stripRedundantSectionHeadingFromBody(
            section.markdown,
            section.sectionId,
            sectionTitle
          )}
          editorRef={editorRef}
          onEditorReady={handleEditorReady}
          onEditorFocus={handleEditorFocus}
          onEditorBlur={onEditorBlur}
          onContentChange={onContentChange}
          onPlaceholderMarksApplied={onPlaceholderMarksApplied}
          imageUpload={
            imageUploadScopeId != null
              ? { complianceId: imageUploadScopeId }
              : undefined
          }
          contentSyncKey={contentSyncKey}
          documentBodyClassName='min-h-[160px] pt-0 pb-4'
          bubbleMenuContent={bubbleMenuContent}
          bubbleMenuHidden={bubbleMenuHidden}
        />
      ) : null}
    </section>
  )
}

export type { SectionEditorRegistration }
